//
// Which auth provider owns a given route.
//
// The Convex client has exactly ONE auth slot. If Clerk and the customer
// provider ever mounted on the same path they would fight over it, and the
// symptom — a token intermittently replaced by the other population's — is
// close to undebuggable. So the two lists live here, side by side, with a
// disjointness assertion that runs in development.
//
// This is a plain module rather than being inline in convex-providers.tsx so
// the routing rule can be asserted behaviourally instead of by reading source
// text.

/**
 * Staff/admin surfaces. Clerk mounts here and nowhere else.
 *
 * `/order/checkout` and `/order/confirmation` used to be here. Both pages have
 * now been ported off `useUser` / `SignInButton` / `SignUpButton` — which throw
 * outside a ClerkProvider, and were the reason the move could not happen
 * earlier — so they sit on the customer list below.
 */
export const CLERK_PATH_PREFIXES = [
  "/admin",
  "/legal",
  "/app",
  "/ops",
  "/checklists",
  "/sign-in",
  "/sign-up",
] as const;

/**
 * Guest surfaces that authenticate with this app's own customer JWT.
 *
 * Online checkout and the order confirmation are here because points can only
 * be spent by a caller Convex can identify: `orders.getMyRewards` resolves
 * through `requireCustomer`, and the customer token only reaches the Convex
 * client's single auth slot on routes this list owns. With them on the Clerk
 * list, a guest signed in at `/account` arrived at checkout anonymous.
 */
export const CUSTOMER_AUTH_PATH_PREFIXES = [
  "/account",
  "/club",
  "/order/checkout",
  "/order/confirmation",
] as const;

/**
 * Prefix match on whole path segments. `startsWith` alone would make `/club`
 * swallow `/cove-club`-style siblings the moment one is added.
 */
export function matchesRoutePrefix(
  pathname: string | null | undefined,
  prefixes: readonly string[],
): boolean {
  if (!pathname) return false;
  return prefixes.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

export function routeNeedsClerk(pathname: string | null | undefined): boolean {
  return matchesRoutePrefix(pathname, CLERK_PATH_PREFIXES);
}

export function routeNeedsCustomerAuth(
  pathname: string | null | undefined,
): boolean {
  return matchesRoutePrefix(pathname, CUSTOMER_AUTH_PATH_PREFIXES);
}

/**
 * Development-only guard against a path that matches both lists. Throws so the
 * mistake shows up on the first render in dev rather than as a token race in
 * production.
 */
export function assertProviderListsAreDisjoint(
  pathname: string | null | undefined,
  // Injectable so the guard itself can be tested against a deliberately
  // overlapping pair. Without that the test can only prove it stays silent,
  // which is exactly what a broken guard also does.
  clerkPrefixes: readonly string[] = CLERK_PATH_PREFIXES,
  customerPrefixes: readonly string[] = CUSTOMER_AUTH_PATH_PREFIXES,
) {
  if (process.env.NODE_ENV === "production") return;
  if (
    matchesRoutePrefix(pathname, clerkPrefixes) &&
    matchesRoutePrefix(pathname, customerPrefixes)
  ) {
    throw new Error(
      `Route "${pathname}" matches both the Clerk and customer-auth provider lists. ` +
        "The Convex client has one auth slot; exactly one provider may own it.",
    );
  }
}
